import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { games } from '../../../mocks/games';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import KeoCoGame from '../components/KeoCoGame';
import CaKheoGame from '../components/CaKheoGame';
import KangGame from '../components/KangGame';
import ComLamGame from '../components/ComLamGame';
import ConOngGame from '../components/ConOngGame';
import CheoThuyenGame from '../components/CheoThuyenGame';

export default function GameDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const game = games.find(g => g.id === id);

  if (!game) {
    return (
      <div className="min-h-screen bg-primary-cream flex items-center justify-center">
        <div className="text-center">
          <p className="text-2xl font-bold text-primary-brown-dark mb-4">Không tìm thấy trò chơi</p>
          <Link to="/games" className="text-primary-orange hover:underline cursor-pointer">← Quay lại danh sách</Link>
        </div>
      </div>
    );
  }

  return <GameDetail game={game} onBack={() => navigate('/games')} />;
}

function playTick() {
  try {
    const AudioCtx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    const ctx = new AudioCtx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.value = 900;
    osc.type = 'square';
    gain.gain.setValueAtTime(0.25, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.08);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.08);
  } catch (_) { /* silence */ }
}

function GameDetail({ game, onBack }: { game: NonNullable<ReturnType<typeof games.find>>; onBack: () => void }) {
  const [phase, setPhase] = useState<'intro' | 'countdown' | 'playing'>('intro');
  const [countdown, setCountdown] = useState(game.countdownSeconds);
  const g = game;

  const startCountdown = () => {
    setPhase('countdown');
    setCountdown(g.countdownSeconds);
    playTick();
  };

  useEffect(() => {
    if (phase !== 'countdown') return;
    if (countdown <= 0) {
      setPhase('playing');
      return;
    }
    playTick();
    const t = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [phase, countdown]);

  if (phase === 'countdown') {
    return <CountdownScreen count={countdown} gameName={g.title} chibiImage={g.chibiImage} />;
  }

  if (phase === 'playing') {
    return <PlayingScreen game={g} onBack={onBack} />;
  }

  return (
    <div className="min-h-screen bg-primary-cream">
      <Header />

      {/* Breadcrumb + Back */}
      <div className="pt-24 pb-4 px-6 max-w-7xl mx-auto flex items-center justify-between">
        <nav className="flex items-center gap-2 text-sm text-primary-brown-light">
          <Link to="/" className="hover:text-primary-orange cursor-pointer">Trang chủ</Link>
          <i className="ri-arrow-right-s-line"></i>
          <Link to="/games" className="hover:text-primary-orange cursor-pointer">Túi trò chơi</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-primary-brown-dark font-medium">{g.title}</span>
        </nav>
        <button
          onClick={onBack}
          className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white border border-primary-brown-light/20 text-sm font-semibold text-primary-brown hover:border-primary-orange hover:text-primary-orange transition-all cursor-pointer whitespace-nowrap"
        >
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-arrow-left-line"></i>
          </div>
          Quay lại
        </button>
      </div>

      {/* Main layout: chibi left + content right (V32 style) */}
      <section className="pb-12 px-6">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-10 items-start">

          {/* LEFT: Chibi + Start Button */}
          <div className="lg:col-span-2 flex flex-col items-center gap-6">
            <div className="bg-gradient-to-b from-primary-yellow/20 to-primary-orange/10 rounded-3xl p-6 w-full flex flex-col items-center">
              <img
                src={g.chibiImage}
                alt={`Nhân vật ${g.title}`}
                className="w-52 h-64 object-cover object-top"
              />
              <h1 className="text-2xl font-extrabold text-primary-brown-dark text-center mt-4 mb-2">{g.title}</h1>
              <div className="flex flex-wrap items-center justify-center gap-3 text-sm text-primary-brown-light mb-5">
                <span className="flex items-center gap-1.5 px-3 py-1.5 bg-white rounded-full font-semibold text-primary-brown-dark">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-time-line text-primary-orange"></i>
                  </div>
                  {g.duration}
                </span>
                <span className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full font-semibold ${g.category === 'Đồng đội' ? 'bg-sky-100 text-sky-700' : 'bg-green-100 text-green-700'}`}>
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className={g.category === 'Đồng đội' ? 'ri-team-line' : 'ri-user-line'}></i>
                  </div>
                  {g.category}
                </span>

              </div>
              <button
                onClick={startCountdown}
                className="w-full py-4 bg-gradient-to-r from-primary-orange to-primary-yellow text-white font-bold text-lg rounded-2xl hover:opacity-90 transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-play-circle-line mr-2"></i>
                Bắt đầu chơi!
              </button>
              <p className="text-xs text-primary-brown-light mt-3 text-center">
                Hãy đọc kỹ hướng dẫn trước khi bắt đầu nhé!
              </p>
            </div>
          </div>

          {/* RIGHT: Content Sections */}
          <div className="lg:col-span-3 space-y-5">

            {/* Giới thiệu */}
            <div className="bg-white rounded-3xl p-6">
              <h2 className="text-lg font-bold text-primary-brown-dark mb-3 flex items-center gap-2">
                <div className="w-8 h-8 flex items-center justify-center bg-primary-orange/10 rounded-full">
                  <i className="ri-information-line text-primary-orange"></i>
                </div>
                Giới thiệu
              </h2>
              <p className="text-primary-brown-light leading-relaxed">{g.description}</p>
            </div>

            {/* Luật chơi */}
            <div className="bg-white rounded-3xl p-6">
              <h2 className="text-lg font-bold text-primary-brown-dark mb-4 flex items-center gap-2">
                <div className="w-8 h-8 flex items-center justify-center bg-red-100 rounded-full">
                  <i className="ri-shield-check-line text-red-500"></i>
                </div>
                Luật chơi
              </h2>
              <div className="space-y-3">
                {g.rules.map((rule, i) => (
                  <div key={i} className="flex items-start gap-3 p-3 rounded-xl bg-red-50">
                    <span className="min-w-5 h-5 flex items-center justify-center bg-red-500 text-white font-bold text-xs rounded-full flex-shrink-0 mt-0.5">
                      {i + 1}
                    </span>
                    <p className="text-sm text-primary-brown leading-relaxed">{rule}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Cách chơi */}
            <div className="bg-white rounded-3xl p-6">
              <h2 className="text-lg font-bold text-primary-brown-dark mb-4 flex items-center gap-2">
                <div className="w-8 h-8 flex items-center justify-center bg-primary-yellow/20 rounded-full">
                  <i className="ri-list-ordered text-primary-yellow"></i>
                </div>
                Cách chơi
              </h2>
              <div className="space-y-4">
                {g.howToPlay.map(item => (
                  <div key={item.step} className="flex items-start gap-4">
                    <span className="min-w-8 h-8 flex items-center justify-center bg-primary-orange text-white font-bold text-sm rounded-full flex-shrink-0">
                      {item.step}
                    </span>
                    <p className="text-primary-brown-light pt-1 text-sm leading-relaxed">{item.description}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Hình ảnh minh họa */}
            <div className="bg-white rounded-3xl p-6">
              <h2 className="text-lg font-bold text-primary-brown-dark mb-4 flex items-center gap-2">
                <div className="w-8 h-8 flex items-center justify-center bg-primary-orange/10 rounded-full">
                  <i className="ri-image-line text-primary-orange"></i>
                </div>
                Hình ảnh minh họa
              </h2>
              <div className="w-full rounded-2xl overflow-hidden">
                <img
                  src={g.image}
                  alt={`Hình minh họa ${g.title}`}
                  className="w-full h-64 object-cover object-center"
                />
              </div>
            </div>

            {/* Lợi ích & Lưu ý */}
            <div className={`grid grid-cols-1 ${g.notes.length > 0 ? 'md:grid-cols-2' : ''} gap-4`}>
              <div className="bg-white rounded-3xl p-6">
                <h2 className="text-base font-bold text-primary-brown-dark mb-3 flex items-center gap-2">
                  <div className="w-7 h-7 flex items-center justify-center bg-green-100 rounded-full">
                    <i className="ri-heart-fill text-green-500 text-sm"></i>
                  </div>
                  Lợi ích
                </h2>
                <ul className="space-y-2">
                  {g.benefits.map((b, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-primary-brown-light">
                      <i className="ri-check-line text-green-500 mt-0.5 flex-shrink-0"></i>
                      {b}
                    </li>
                  ))}
                </ul>
              </div>
              {g.notes.length > 0 && (
                <div className="bg-white rounded-3xl p-6">
                  <h2 className="text-base font-bold text-primary-brown-dark mb-3 flex items-center gap-2">
                    <div className="w-7 h-7 flex items-center justify-center bg-amber-100 rounded-full">
                      <i className="ri-alert-line text-amber-500 text-sm"></i>
                    </div>
                    Lưu ý
                  </h2>
                  <ul className="space-y-2">
                    {g.notes.map((n, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-primary-brown-light">
                        <i className="ri-error-warning-line text-amber-500 mt-0.5 flex-shrink-0"></i>
                        {n}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function CountdownScreen({ count, gameName, chibiImage }: { count: number; gameName: string; chibiImage: string }) {
  return (
    <div className="min-h-screen bg-gradient-to-b from-primary-yellow/30 to-primary-orange/20 flex items-center justify-center">
      <div className="text-center">
        <img
          src={chibiImage}
          alt="Nhân vật"
          className="w-48 h-56 object-cover object-top mx-auto mb-6"
        />
        <p className="text-xl font-semibold text-primary-brown-dark mb-6">
          Chuẩn bị chơi <strong>{gameName}</strong>!
        </p>
        <div className="relative inline-flex items-center justify-center">
          <div
            key={count}
            className="w-32 h-32 flex items-center justify-center bg-white rounded-full text-6xl font-bold text-primary-orange"
            style={{ animation: 'pulse 0.9s ease-in-out' }}
          >
            {count > 0 ? count : '🎉'}
          </div>
        </div>
        <p className="mt-6 text-primary-brown-light font-medium">
          {count > 0 ? 'Giây nữa sẽ bắt đầu...' : 'Bắt đầu thôi!'}
        </p>
      </div>
    </div>
  );
}

function PlayingScreen({ game, onBack }: { game: NonNullable<ReturnType<typeof games.find>>; onBack: () => void }) {
  if (game.id === 'keo-co') return <KeoCoGame onBack={onBack} />;
  if (game.id === 'thi-ca-kheo-len-doc') return <CaKheoGame onBack={onBack} />;
  if (game.id === 'kang') return <KangGame onBack={onBack} />;
  if (game.id === 'thi-nuong-com-lam') return <ComLamGame onBack={onBack} />;
  if (game.id === 'con-ong') return <ConOngGame onBack={onBack} />;
  if (game.id === 'cheo-thuyen-doc-moc') return <CheoThuyenGame onBack={onBack} />;

  return (
    <div className="min-h-screen bg-primary-cream flex items-center justify-center px-6">
      <div className="max-w-2xl w-full text-center">
        <img
          src={game.chibiImage}
          alt="Nhân vật"
          className="w-44 h-52 object-cover object-top mx-auto mb-6"
        />
        <h2 className="text-3xl font-bold text-primary-brown-dark mb-3">{game.title}</h2>
        <div className="bg-white rounded-3xl p-8 mb-6">
          <div className="w-16 h-16 flex items-center justify-center bg-primary-orange/10 rounded-full mx-auto mb-4">
            <i className="ri-play-circle-fill text-4xl text-primary-orange"></i>
          </div>
          <p className="text-primary-brown-light text-lg mb-2">Trò chơi đang trong quá trình phát triển!</p>
          <p className="text-sm text-primary-brown-light">Tính năng chơi tương tác sẽ sớm được ra mắt. Hãy cùng bạn bè thực hành theo hướng dẫn nhé!</p>
        </div>
        <button
          onClick={onBack}
          className="px-8 py-3 bg-gradient-to-r from-primary-orange to-primary-yellow text-white font-bold rounded-2xl hover:opacity-90 transition-all cursor-pointer whitespace-nowrap"
        >
          <i className="ri-arrow-left-line mr-1"></i> Quay lại danh sách trò chơi
        </button>
      </div>
    </div>
  );
}
